import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import type { DemandStabilityData } from '../../../lib/api/scheduled-dashboard';

const ADDED_COLOR = '#0f766e';
const REMOVED_COLOR = '#dc2626';

const SERIES_LABELS: Record<string, string> = {
  added: 'Added since baseline',
  removed: 'Removed since baseline',
};

function weekLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  // UTC, as parsed: in the viewer's zone a viewer west of UTC reads each week a
  // day early (the week of 24 Aug labelled 23 Aug). Same as ForecastMovementChart.
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

interface ChartRow {
  week: string;
  added: number;
  /** Negative, so it stacks below zero. */
  removed: number;
  /** Total currently scheduled — shown in the tooltip. */
  current: number;
}

interface DemandMovementChartProps {
  data: DemandStabilityData | null;
}

/**
 * Forward demand movement, one diverging bar per delivery week from this
 * week onwards: qty added since the baseline above zero (green), qty
 * removed below (red). The week's total scheduled qty shows in the
 * tooltip. Deltas are computed per part per week server-side, so opposite
 * moves on different parts don't cancel.
 */
export function DemandMovementChart({ data }: DemandMovementChartProps) {
  const rows: ChartRow[] = useMemo(
    () =>
      (data?.forward?.weeks ?? []).map((w) => ({
        week: weekLabel(w.week),
        added: w.added,
        removed: -w.removed,
        current: w.current,
      })),
    [data],
  );
  const empty = rows.every((r) => r.added === 0 && r.removed === 0 && r.current === 0);
  const baseline = data?.forward?.baseline ? weekLabel(data.forward.baseline) : null;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-700">Demand Movement — This Week Onwards</CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Qty added to (▲) and removed from (▼) each forward delivery week
          {baseline ? ` since ${baseline}` : ''}. Hover a week for its total scheduled qty.
        </p>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="h-96 flex items-center justify-center text-sm text-muted-foreground">
            No forward demand in scope — adjust the supplier / part filters.
          </div>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 5, right: 10, bottom: 0, left: -10 }} stackOffset="sign">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} interval={1} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    Math.abs(value).toLocaleString(),
                    SERIES_LABELS[name] ?? name,
                  ]}
                  labelFormatter={(label: string, items: ReadonlyArray<{ payload?: ChartRow }>) => {
                    const current = items?.[0]?.payload?.current;
                    return current != null
                      ? `Week of ${label} — ${current.toLocaleString()} scheduled`
                      : `Week of ${label}`;
                  }}
                />
                <Legend
                  formatter={(v: string) => SERIES_LABELS[v] ?? v}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar dataKey="added" stackId="mv" fill={ADDED_COLOR} isAnimationActive={false} maxBarSize={28} />
                <Bar dataKey="removed" stackId="mv" fill={REMOVED_COLOR} isAnimationActive={false} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
